import React from 'react';
import { formatCurrency } from '../../utils/formatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CapExSystemItem {
  name: string;
  cost: number;
  pctOfBudget: number;
  scope: string;
  rentImpact: string;       // e.g. "+$150/unit/mo" or "Deferred maint."
  rentImpactValue?: number;  // Numeric rent impact for highlighting
  priority: 'HIGH' | 'MED' | 'LOW';
}

export interface CapExSystemTableProps {
  totalCapex: number;
  totalUnits: number;
  totalSqft: number;
  annualRentUplift: number;
  perSystemBreakdown?: CapExSystemItem[];
}

// ---------------------------------------------------------------------------
// Default allocation (matches wireframe)
// ---------------------------------------------------------------------------

const DEFAULT_SYSTEMS: Omit<CapExSystemItem, 'cost' | 'pctOfBudget'>[] = [
  { name: 'Kitchen', scope: 'Full gut: cabinets, counters, appliances, tile', rentImpact: '+$150/unit/mo', rentImpactValue: 150, priority: 'HIGH' },
  { name: 'Bathroom', scope: 'Vanity, tile surround, fixture update', rentImpact: '+$75/unit/mo', rentImpactValue: 75, priority: 'HIGH' },
  { name: 'Flooring', scope: 'LVP throughout', rentImpact: '+$50/unit/mo', rentImpactValue: 50, priority: 'HIGH' },
  { name: 'HVAC', scope: 'Mini-split system', rentImpact: 'Deferred maint.', priority: 'MED' },
  { name: 'Electrical', scope: 'Panel upgrade, separate meters', rentImpact: 'Compliance', priority: 'MED' },
  { name: 'Exterior / Paint', scope: 'Exterior paint, minor siding repair', rentImpact: 'Curb appeal', priority: 'LOW' },
  { name: 'Contingency (5%)', scope: 'Unexpected costs reserve', rentImpact: '\u2014', priority: 'LOW' },
];

const DEFAULT_ALLOC = [0.333, 0.190, 0.143, 0.119, 0.095, 0.071, 0.048];

function buildDefaultSystems(totalCapex: number, units: number): CapExSystemItem[] {
  return DEFAULT_SYSTEMS.map((sys, i) => {
    const cost = Math.round(totalCapex * DEFAULT_ALLOC[i]);
    const label = units > 1 ? `${sys.name} (x${units})` : sys.name;
    return {
      ...sys,
      name: label,
      cost,
      pctOfBudget: DEFAULT_ALLOC[i],
    };
  });
}

// ---------------------------------------------------------------------------
// Priority badge
// ---------------------------------------------------------------------------

function PriorityBadge({ priority }: { priority: 'HIGH' | 'MED' | 'LOW' }) {
  const colors = {
    HIGH: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    MED: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    LOW: 'bg-stone-700/40 text-stone-700 border-stone-600/30',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-sm font-semibold tracking-wider uppercase rounded border ${colors[priority]}`}>
      {priority}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CapExSystemTable: React.FC<CapExSystemTableProps> = ({
  totalCapex,
  totalUnits,
  totalSqft,
  annualRentUplift,
  perSystemBreakdown,
}) => {
  const systems = perSystemBreakdown ?? buildDefaultSystems(totalCapex, totalUnits);
  const totalRentImpactPerMonth = systems.reduce(
    (sum, s) => sum + (s.rentImpactValue ?? 0),
    0,
  );
  const capexPerUnit = totalUnits > 0 ? totalCapex / totalUnits : 0;
  const capexPerSqft = totalSqft > 0 ? totalCapex / totalSqft : 0;
  const paybackMonths = annualRentUplift > 0 ? (totalCapex / annualRentUplift) * 12 : null;
  const roiPct = totalCapex > 0 ? annualRentUplift / totalCapex : 0;

  return (
    <div className="bg-charcoal/60 border border-slate-dark rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-dark flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ivory">
            Renovation Budget — {formatCurrency(totalCapex)} Total ({formatCurrency(capexPerUnit)}/unit)
          </h3>
        </div>
        <span className="text-sm font-semibold tracking-widest uppercase text-gold bg-gold/10 border border-gold/20 px-2 py-0.5 rounded">
          System-by-System
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-sm font-semibold tracking-widest uppercase text-stone-700 border-b border-stone-800">
              <th className="text-left px-4 py-2.5">System</th>
              <th className="text-right px-4 py-2.5">Cost</th>
              <th className="text-right px-4 py-2.5">% of Budget</th>
              <th className="text-left px-4 py-2.5">Scope</th>
              <th className="text-left px-4 py-2.5">Rent Impact</th>
              <th className="text-center px-4 py-2.5">Priority</th>
            </tr>
          </thead>
          <tbody>
            {systems.map((sys, i) => (
              <tr key={i} className="border-b border-stone-800/50 hover:bg-stone-800/20 transition-colors">
                <td className="px-4 py-2.5 text-ivory font-medium">{sys.name}</td>
                <td className="px-4 py-2.5 text-right text-stone-300 tabular-nums">{formatCurrency(sys.cost)}</td>
                <td className="px-4 py-2.5 text-right text-stone-700 tabular-nums">{(sys.pctOfBudget * 100).toFixed(1)}%</td>
                <td className="px-4 py-2.5 text-stone-700 text-sm">{sys.scope}</td>
                <td className={`px-4 py-2.5 text-sm ${sys.rentImpactValue ? 'text-emerald-400 font-medium' : 'text-stone-700'}`}>
                  {sys.rentImpact}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <PriorityBadge priority={sys.priority} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gold/5 border-t border-gold/20">
              <td className="px-4 py-2.5 font-bold text-gold">TOTAL</td>
              <td className="px-4 py-2.5 text-right font-bold text-gold tabular-nums">{formatCurrency(totalCapex)}</td>
              <td className="px-4 py-2.5 text-right font-bold text-stone-300">100%</td>
              <td className="px-4 py-2.5" />
              <td className="px-4 py-2.5 text-emerald-400 font-semibold text-sm">
                {totalRentImpactPerMonth > 0 ? `+$${totalRentImpactPerMonth}/mo total` : '\u2014'}
              </td>
              <td className="px-4 py-2.5" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-3 gap-px bg-stone-800/50 border-t border-stone-800">
        <div className="bg-charcoal/80 px-4 py-3 text-center">
          <div className="text-sm text-stone-700 uppercase tracking-wider">CapEx / Unit</div>
          <div className="text-base font-semibold text-ivory mt-1 tabular-nums">{formatCurrency(capexPerUnit)}</div>
        </div>
        <div className="bg-charcoal/80 px-4 py-3 text-center">
          <div className="text-sm text-stone-700 uppercase tracking-wider">CapEx / SqFt</div>
          <div className="text-base font-semibold text-ivory mt-1 tabular-nums">
            {capexPerSqft > 0 ? `$${capexPerSqft.toFixed(2)}` : '\u2014'}
          </div>
        </div>
        <div className="bg-charcoal/80 px-4 py-3 text-center">
          <div className="text-sm text-stone-700 uppercase tracking-wider">Payback Period</div>
          <div className={`text-base font-semibold mt-1 tabular-nums ${paybackMonths && paybackMonths < 18 ? 'text-emerald-400' : 'text-ivory'}`}>
            {paybackMonths ? `${paybackMonths.toFixed(1)} mo` : '\u2014'}
          </div>
          {paybackMonths && (
            <div className="text-sm text-stone-700 mt-0.5">
              {formatCurrency(totalCapex)} / ({formatCurrency(annualRentUplift)}/yr uplift)
            </div>
          )}
        </div>
      </div>

      {/* Formula block */}
      <div className="px-4 py-3 border-t border-stone-800 bg-stone-900/40 text-sm text-stone-700 font-mono">
        <span className="text-gold font-semibold">Gross Rent Yield</span> = Annual Rent Uplift / CapEx
        = <span className="text-ivory">{formatCurrency(annualRentUplift)}</span>
        {' / '}
        <span className="text-ivory">{formatCurrency(totalCapex)}</span>
        = <span className={`font-semibold ${roiPct > 0.08 ? 'text-emerald-400' : 'text-ivory'}`}>
          {(roiPct * 100).toFixed(1)}%
        </span>
        <span className="text-stone-700 font-sans normal-case ml-2">
          (before vacancy &amp; OpEx — see CapEx ROI in Return on CapEx section)
        </span>
      </div>
    </div>
  );
};
